/* eslint-disable */
import React, { PureComponent } from 'react';
import axios from 'axios';
import { TFunction } from 'i18next';
import { toast } from 'react-toastify';

import { Authenticated, createAuthHeaders } from '../../app/AuthorizationProvider';
import { useConfig } from '../../app/ConfigurationProvider';
import { HistoricalDataIntervalType } from './utils';
import { isUnauthorized } from '../../utils/customFetch';

interface DataImporterState {
  file: File | null;
  uploadPending: boolean;
  uploadProgress: number;
}

export interface DataImporterPassedProps {
  onFileChange: (file: File) => void;
  fileName: string | null;
  fileSize: string | null;
  onSubmit: (historicalDataInterval: HistoricalDataIntervalType) => void;
  onCancel: () => void;
  submitDisabled: boolean;
  cancelDisabled: boolean;
  uploadPending: boolean;
  uploadProgress: number;
}

interface DataImportFormProps {
  t: TFunction;
  children: (passedProps: DataImporterPassedProps) => React.ReactNode;
  config: ReturnType<typeof useConfig>;
  isTokenInvalid: Authenticated['isTokenInvalid'];
  keycloak: Authenticated['keycloak'];
  locale: string;
  forceUpdateToken: Authenticated['forceUpdateToken'];
  onUploadFinish: () => void;
}

export class DataImporter extends PureComponent<DataImportFormProps, DataImporterState> {
  public state = {
    file: null,
    uploadPending: false,
    uploadProgress: 0,
  };

  public render() {
    const { children, locale } = this.props;
    const { file, uploadPending, uploadProgress } = this.state;
    const fileName = file ? ((file as unknown) as File).name : null;
    const fileSizeNumerical =
      file && (((file as unknown) as File).size / 1000000).toLocaleString(locale, { maximumFractionDigits: 2 });
    const fileSize = fileSizeNumerical ? `${fileSizeNumerical}MB` : null;
    const passedProps: DataImporterPassedProps = {
      fileName,
      fileSize,
      uploadPending,
      uploadProgress,
      onCancel: this.onCancel,
      onSubmit: this.onSubmit,
      onFileChange: this.onFileChange,
      submitDisabled: !fileName,
      cancelDisabled: !fileName,
    };

    return children(passedProps);
  }

  private onFileChange = (file: File) => {
    const { name, size } = file;
    const over2MB = size / 1000000 > 2.0;
    const invalidExtension = !name.endsWith('.csv');

    if (invalidExtension) {
      const message = this.props.t('dataImport.invalidFormat');
      toast.warning(message);
    } else if (over2MB) {
      const message = this.props.t('dataImport.fileTooLarge');
      toast.warning(message);
    } else {
      this.setState({ file });
    }
  };

  private onSubmit = async (historicalDataInterval: HistoricalDataIntervalType) => {
    const {
      config: { restPath, tenant },
      forceUpdateToken,
      isTokenInvalid,
      t,
    } = this.props;
    const { file } = this.state;

    if (!file) {
      return;
    }

    this.setState({ uploadPending: true, uploadProgress: 0 });

    const url = `${restPath}forecasting/historical-data/historical-data-upload`;

    const formData = new FormData();
    formData.append('file', file!);
    formData.append('historicalDataInterval', historicalDataInterval.toString());

    if (isTokenInvalid()) {
      console.debug('Token was evaluated as invalid and needs to be re-fetched before request will be executed.');
      await forceUpdateToken();
    }

    const config = {
      onUploadProgress: (ProgressEvent: ProgressEvent) => {
        const percentage = Math.round((ProgressEvent.loaded / ProgressEvent.total) * 100);
        this.setState({ uploadProgress: Math.min(percentage, 95) });
      },
      headers: {
        // Need to access token from Keycloak instance directly, as it might have changed after force update
        ...createAuthHeaders(this.props.keycloak.token!, tenant),
      },
    };

    axios
      .post(url, formData, config)
      .then(() => {
        // Success
        toast.success(t('response.success.dataImport.POST'));
        this.setState({ uploadProgress: 100 });
      })
      .catch(async (error) => {
        if (error.response) {
          // The request was made and the server responded with a status code out of the range of 2xx

          this.setState({ uploadProgress: 0 });

          if (isUnauthorized(error.response.status)) {
            const refreshTokenSucceed = await forceUpdateToken();

            if (refreshTokenSucceed) {
              console.debug('Token was successfully renewed after 401, trying to re-upload file.');
              this.onSubmit(historicalDataInterval);
            }
            return;
          }

          if (error.response.status >= 500) {
            toast.error(t(`response.unavailable`));
          } else {
            const message = error.response.data.errorCode;

            if (message === 'fe.for.histData.upload.dataIntervalPrevious') {
              const errors = error.response.data.relatedEntities.map((entity: any) => {
                const { name, id } = entity.values[0];
                const interval = t(`dataImport.historicalDataInterval.${id}`);

                return `${name} (${interval})`;
              });

              toast.warning(t(`response.${message}`, { errors: errors.join(', ') }));
            } else {
              toast.warning(t(`response.${message}`));
            }
          }
        } else if (error.request) {
          // The request was made but no response was received or network error - no connection
          toast.error(t(`response.unavailable`));
        }
      })
      .finally(() => {
        // To animate final 100% or 0% progress
        setTimeout(() => {
          this.setState({
            file: null,
            uploadPending: false,
          });
        }, 1000);

        this.props.onUploadFinish();
      });
  };

  private onCancel = () => this.setState({ file: null });
}
